import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/api";
import { CalendarDays, MapPin, Users, ShieldCheck, ArrowRight } from "lucide-react";

export const dateRange = (a, b) => {
  const f = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return a === b ? f(a) : `${f(a)} – ${f(b)}`;
};

export default function Camps() {
  const [camps, setCamps] = useState([]);
  const [city, setCity] = useState("");

  useEffect(() => {
    api.get("/camps", { params: city ? { city } : {} }).then((r) => setCamps(r.data)).catch(() => {});
  }, [city]);

  const cities = [...new Set(camps.map((c) => c.city))];

  return (
    <div data-testid="camps-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Multi-day</span>
      <h1 className="display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter font-bold mt-3 leading-[0.95]">Goalie camps</h1>
      <p className="text-base text-zinc-300 mt-6 max-w-xl leading-relaxed">Multi-day camps run by verified coaches. One purchase covers the whole camp — ice, off-ice and video sessions included.</p>

      {cities.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-8">
          {[{ v: "", l: "All cities" }, ...cities.map((c) => ({ v: c, l: c }))].map((c) => (
            <button key={c.v || "all"} data-testid={`camp-city-${c.v ? c.v.toLowerCase().replace(/[^a-z]+/g, "-") : "all"}`} onClick={() => setCity(c.v)}
              className={`text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors duration-200 ${
                city === c.v ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 text-zinc-400 hover:border-white/40"}`}>
              {c.l}
            </button>
          ))}
        </div>
      )}

      {camps.length === 0 ? (
        <div data-testid="no-camps" className="mt-12 border border-white/10 p-12 text-center">
          <p className="text-sm text-zinc-400">No camps published yet. Check back soon.</p>
          <Link to="/coaches" className="inline-block mt-6 bg-[#00e5ff] text-black text-xs font-bold uppercase px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Browse coaches instead</Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {camps.map((c) => (
            <Link key={c.id} to={`/camps/${c.id}`} data-testid={`camp-card-${c.id}`}
              className="group border border-white/10 bg-[#121212] p-6 flex flex-col hover:border-[#00e5ff]/50 transition-colors duration-200">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#00e5ff]">
                <CalendarDays className="w-3 h-3" /> {dateRange(c.start_date, c.end_date)}
              </div>
              <h2 className="display text-2xl uppercase tracking-tight font-bold mt-3 leading-tight">{c.title}</h2>
              <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {c.city}{c.region ? `, ${c.region}` : ""}{c.venue ? ` · ${c.venue}` : ""}
              </p>
              <p className="text-sm text-zinc-400 mt-4 leading-relaxed line-clamp-3">{c.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {c.levels.slice(0, 3).map((l) => (
                  <span key={l} className="text-[10px] uppercase tracking-wide border border-white/10 text-zinc-300 px-2 py-1">{l}</span>
                ))}
              </div>
              <div className="mt-auto pt-5 flex items-end justify-between border-t border-white/5 mt-5">
                <div>
                  <span className="text-[10px] uppercase text-zinc-500 block flex items-center gap-1">
                    {c.coach_name} {c.verified && <ShieldCheck className="w-3 h-3 text-[#00e5ff]" />}
                  </span>
                  <span className="display text-3xl font-bold text-[#00e5ff] leading-none">{money(c.price)}</span>
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1 mt-1">
                    <Users className="w-3 h-3" /> {c.spots_left} of {c.capacity} spots left
                  </span>
                </div>
                <span className="text-[11px] uppercase tracking-widest text-[#00e5ff] flex items-center gap-1">
                  Details <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-200" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
